import { getDb, replacePersonStores } from './db';
import { SCHEMA_VERSION } from './migrations';
import { DEFAULT_SETTINGS } from './seed/defaults';
import { de } from '../i18n/de';
import type {
  Abbrev,
  CalibrationRun,
  CandidatePhrase,
  Category,
  HistoryEntry,
  Phrase,
  QuickFire,
  Settings,
} from '../core/types';

/**
 * The durability contract (`specs/10-contracts.md`).
 *
 * One versioned, human-readable JSON document. It is the unit of backup, restore and
 * device migration, and the escape hatch for every future decision — so it is
 * deliberately boring: no compression, no binary, no cleverness. Somebody may have to
 * read this file in a text editor years from now to get a person their voice back.
 *
 * Audio is excluded on purpose. It is regenerable from the text and would bloat every
 * backup, so import leaves the audio cache empty and the pre-render pass
 * (opportunistic when online, or the caregiver's "Audio vorbereiten") refills it. A
 * restored device therefore needs one online moment before it is fully
 * offline-capable — that belongs in the restore runbook.
 *
 * Diagnostics are excluded too: derived metadata, not the person's data.
 */

export const EXPORT_FORMAT = 'sags-export' as const;

/**
 * What an imported n-gram claims about when it was last used: **long ago**.
 *
 * The file carries `[word, count]` tuples and no per-word timestamp, so the import has to put
 * *something* in `lastUsedAt`, and the choice is not cosmetic. Stamping `exportedAt` — "used just
 * now", which is what this did originally — hands every restored word the full recency term:
 *
 *   w₅ · recencyBoost(0) = 1 · 1.0 = 1.00
 *
 * against a personal-count contribution of `w₂ · normLog(1, 50) = 0.53` for a word said once, and
 * `w₁ · normLog(30 000, 1 000 000) = 0.75` for a common word like "wir". So a word the person said
 * once, months before the backup, would outrank "wir" at its own prefix for the first twelve hours
 * after a restore — the C42 failure mode again, at the one moment somebody is watching the row to
 * decide whether the restore worked.
 *
 * The epoch says the only thing the data supports: we do not know when this word was last used, so it
 * gets no recency credit until the person actually says it again. `recencyBoost` decays to 0 long
 * before 1970, so the term contributes nothing and the counts — the part that took months — decide the
 * ranking on their own.
 */
export const IMPORTED_LAST_USED = new Date(0).toISOString();

export interface SagsExport {
  format: typeof EXPORT_FORMAT;
  schemaVersion: number;
  exportedAt: string;
  settings: Settings;
  quickFires: QuickFire[];
  categories: Category[];
  phrases: Phrase[];
  abbrevs: Abbrev[];
  personalUnigrams: [string, number][];
  personalBigrams: [string, string, number][];
  candidatePhrases: CandidatePhrase[];
  calibrationRuns: CalibrationRun[];
  history: HistoryEntry[];
}

export class ImportError extends Error {
  /**
   * Which field failed. **Never displayed:** a JSON field name is English (INV-05), and `history` on
   * screen tells a caregiver nothing they can act on. This is for the console and for the tests.
   */
  readonly field?: string | undefined;

  /** German, because it is shown to a caregiver (INV-05). */
  constructor(message: string, field?: string, cause?: unknown) {
    super(message, { cause });
    this.name = 'ImportError';
    this.field = field;
  }
}

export async function buildExport(): Promise<SagsExport> {
  const db = await getDb();

  const [
    settings,
    quickFires,
    categories,
    phrases,
    abbrevs,
    unigrams,
    bigrams,
    candidatePhrases,
    calibrationRuns,
    history,
  ] = await Promise.all([
    db.get('settings', 'settings'),
    db.getAll('quickFires'),
    db.getAll('categories'),
    db.getAll('phrases'),
    db.getAll('abbrevs'),
    db.getAll('personalUnigrams'),
    db.getAll('personalBigrams'),
    db.getAll('candidatePhrases'),
    db.getAll('calibration'),
    db.getAll('history'),
  ]);

  if (!settings) throw new Error('cannot export before settings exist');

  return {
    format: EXPORT_FORMAT,
    schemaVersion: SCHEMA_VERSION,
    exportedAt: new Date().toISOString(),
    settings,
    quickFires,
    categories,
    phrases,
    abbrevs,
    // Tuples rather than objects: n-grams are the bulk of a mature backup, and this
    // keeps the file readable at a few thousand entries. The counts are what took months to
    // accumulate and they round-trip exactly; per-word `lastUsedAt` does not survive, and the import
    // is careful about what it claims instead — see `IMPORTED_LAST_USED`.
    personalUnigrams: unigrams.map((row) => [row.word, row.count]),
    personalBigrams: bigrams.map((row) => [row.prev, row.next, row.count]),
    candidatePhrases,
    calibrationRuns,
    history,
  };
}

export function serialiseExport(data: SagsExport): Blob {
  return new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
}

export function exportFilename(exportedAt: string): string {
  return `sags-backup-${exportedAt.slice(0, 10)}.json`;
}

/**
 * ── Validating an untrusted file (C74, DAT-11) ──
 *
 * `parseExport` used to check the envelope and four of the nine arrays, and `applyImport` cleared the
 * device before iterating the other five. A caregiver hand-authors this file from a spreadsheet, so
 * the omissions are not hypothetical — and the failure was always data loss: a `TypeError` from a loop
 * header let the write transaction auto-commit half a device, and a rejected `put` aborted it and left
 * nothing. Both reached the caregiver as English.
 *
 * The list of arrays is **derived from the document type** and the rules are a `Record` over it, so
 * adding an array to `SagsExport` without saying how to check it is a compile error. That is the actual
 * defect: nothing made the list of arrays and the list of checks be the same list.
 */
type ExportArray = {
  [K in keyof SagsExport]: SagsExport[K] extends readonly unknown[] ? K : never;
}[keyof SagsExport];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * A value IndexedDB will accept as a key, and that a reader can find again.
 *
 * `undefined` makes `put` throw synchronously from inside the loop, which is the auto-commit hazard.
 * The empty string is a legal key and a useless one: that row could never be looked up, edited or
 * deleted again.
 */
const isKey = (value: unknown): value is string => typeof value === 'string' && value.length > 0;

const isText = (value: unknown): value is string => typeof value === 'string';

/**
 * A number, not a string that looks like one. `"19"` is what a spreadsheet column produces, and it
 * survives every arithmetic in this app as a silent `NaN` — including `a.order - b.order`, where a
 * comparator returning `NaN` leaves the sort free to produce any order at all.
 */
const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const isBool = (value: unknown): value is boolean => typeof value === 'boolean';

const optional = (value: unknown, check: (candidate: unknown) => boolean): boolean =>
  value === undefined || check(value);

/**
 * What each row must carry: its key path, its index paths, and every field a reader dereferences
 * without a guard. Optional fields are checked only when present.
 *
 * An **unknown field is never a reason to refuse.** An older export legitimately lacks fields added
 * since, and a spare column in a hand-authored file is not a corrupt file.
 */
const ROW_RULES: Record<ExportArray, (row: unknown) => boolean> = {
  quickFires: (row) =>
    isRecord(row) &&
    isKey(row.id) &&
    isText(row.text) &&
    isNumber(row.order) &&
    optional(row.audioKey, isText),

  categories: (row) =>
    isRecord(row) &&
    isKey(row.id) &&
    isText(row.name) &&
    optional(row.colour, isKey) &&
    isNumber(row.order) &&
    optional(row.icon, isText) &&
    optional(row.locked, isBool),

  // `categoryId` is the `byCategory` index path, and that index is how the Sätze screen reads: a
  // phrase without one is in the store and on no screen, which a caregiver could only find by
  // counting.
  phrases: (row) =>
    isRecord(row) &&
    isKey(row.id) &&
    isKey(row.categoryId) &&
    isText(row.text) &&
    isNumber(row.order) &&
    isBool(row.pinned) &&
    isNumber(row.useCount) &&
    optional(row.shortLabel, isText) &&
    optional(row.icon, isText) &&
    optional(row.lastUsedAt, isText) &&
    optional(row.audioKey, isText) &&
    optional(row.locked, isBool),

  abbrevs: (row) => isRecord(row) && isKey(row.code) && isText(row.expansion),

  // Tuples rather than objects, to keep a mature backup readable — which means "one element short" is
  // exactly what a spreadsheet produces. Unchecked, `['wort']` *succeeded*: it wrote
  // `{ word: 'wort', count: undefined }` into a field typed `number`, and that word scored `NaN` for
  // ever afterwards. Silent corruption is the one outcome worse than a refused file.
  personalUnigrams: (row) =>
    Array.isArray(row) && row.length === 2 && isKey(row[0]) && isNumber(row[1]),

  // Compound key path `['prev', 'next']`: either half missing is a `DataError` from `put`.
  personalBigrams: (row) =>
    Array.isArray(row) && row.length === 3 && isKey(row[0]) && isKey(row[1]) && isNumber(row[2]),

  candidatePhrases: (row) =>
    isRecord(row) &&
    isKey(row.text) &&
    isNumber(row.count) &&
    isText(row.firstSeen) &&
    isText(row.lastSeen) &&
    isBool(row.dismissed),

  calibrationRuns: (row) =>
    isRecord(row) &&
    isKey(row.id) &&
    isText(row.ranAt) &&
    Array.isArray(row.trials) &&
    isRecord(row.derivedSettings) &&
    Array.isArray(row.recommendations) &&
    row.recommendations.every(isText) &&
    isBool(row.provisional) &&
    isBool(row.accepted),

  // `source` is checked as a string, not against its four-value union: an unrecognised label is
  // skipped by `MINEABLE_SOURCES` and breaks nothing, and refusing a whole restore over one history
  // row's label would trade the person's vocabulary for a value nothing dereferences.
  history: (row) =>
    isRecord(row) &&
    isKey(row.id) &&
    isText(row.text) &&
    isText(row.spokenAt) &&
    isText(row.source),
};

/** The nine, so a caller cannot forget one. */
const EXPORT_ARRAYS = Object.keys(ROW_RULES) as ExportArray[];

function refuse(field: string, why: string): never {
  // The field name and the reason go to the console; the caregiver gets German. Both matter: for a
  // file derived from a spreadsheet, "phrases[143]" is the difference between fixing it and retyping
  // it, and it is not a sentence anybody can act on in a status line.
  console.warn(`[import] refused: ${field} ${why}`);
  throw new ImportError(de.storageImportIncomplete, field);
}

/**
 * Check the named arrays, row by row.
 *
 * Exported because a content-only import (`AUT-07`) validates a subset of the nine — "validation
 * first, then the merge" is that requirement's stated order.
 */
export function validateRows(
  document_: Record<string, unknown>,
  fields: readonly ExportArray[],
): void {
  for (const field of fields) {
    const rows = document_[field];
    if (!Array.isArray(rows)) refuse(field, 'is missing or is not an array');
    const bad = rows.findIndex((row) => !ROW_RULES[field](row));
    if (bad !== -1) refuse(`${field}[${bad}]`, 'is not a row we can write');
  }
}

/**
 * The settings fields whose `typeof` says `object` and means something narrower. Every other field is
 * checked against the type of its own default, so a new setting is checked the moment it has one —
 * there is no second list to keep in step.
 *
 * Exported for the guard test that asserts this list and `DEFAULT_SETTINGS` cannot drift apart.
 */
export const SETTINGS_SHAPES: Partial<Record<keyof Settings, (value: unknown) => boolean>> = {
  pin: (value) => value === null || isText(value),
  lowEffortRestore: (value) => value === null || isRecord(value),
  predictionWeights: (value) => Array.isArray(value) && value.every(isNumber),
  touchOffsetCorrection: (value) => isRecord(value) && isNumber(value.x) && isNumber(value.y),
};

/**
 * Two fields that must be usable rather than merely numeric. Zero is what an empty spreadsheet cell
 * becomes, and at `pxPerMm` or `keyMm` zero every key computes to 0 px — a blank screen somebody is
 * expected to speak with. Ranges beyond this belong to the settings panel.
 */
const POSITIVE_SETTINGS: readonly (keyof Settings)[] = ['pxPerMm', 'keyMm'];

/**
 * Type-check what is present, then fill the rest from the defaults.
 *
 * **Merged rather than required, and for a reason that is not tolerance.** Five settings fields were
 * added after Phase 1, and `parseExport` promises to accept any schema up to the current one — so a
 * strict "every field required" rule would refuse a genuine v1 export, which is the file this whole
 * requirement exists to protect. `settingsRepo.get()` already merges over these defaults for exactly
 * this reason: the app must boot with a complete configuration even if the stored row predates half of
 * it.
 *
 * The merge is over the **defaults**, never over the device — the device's own row is cleared in the
 * same transaction — so `settingsRepo.replace`'s "must not merge with stale values" still holds.
 */
function normaliseSettings(value: unknown): Settings {
  // `typeof [] === 'object'`, which is how an array of nothing came to be valid settings.
  if (!isRecord(value)) refuse('settings', 'is not an object');

  for (const [field, provided] of Object.entries(value)) {
    if (!(field in DEFAULT_SETTINGS)) continue; // a spare column is not a reason to refuse a file
    const key = field as keyof Settings;
    const shape = SETTINGS_SHAPES[key];
    const ok = shape
      ? shape(provided)
      : typeof provided === typeof DEFAULT_SETTINGS[key] && provided !== null;
    if (!ok) refuse(`settings.${field}`, 'has the wrong type');
    if (POSITIVE_SETTINGS.includes(key) && !(isNumber(provided) && provided > 0)) {
      refuse(`settings.${field}`, 'must be greater than zero');
    }
  }

  return { ...DEFAULT_SETTINGS, ...(value as Partial<Settings>) };
}

/**
 * Validate an untrusted file. Structural only — enough to be sure we are not about to
 * wipe a working device for a file that was never ours.
 */
export function parseExport(json: string): SagsExport {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new ImportError(de.storageImportInvalid);
  }

  if (!parsed || typeof parsed !== 'object') throw new ImportError(de.storageImportInvalid);
  const candidate = parsed as Partial<SagsExport>;

  if (candidate.format !== EXPORT_FORMAT) throw new ImportError(de.storageImportInvalid);
  if (typeof candidate.schemaVersion !== 'number') throw new ImportError(de.storageImportInvalid);
  if (candidate.schemaVersion > SCHEMA_VERSION) {
    throw new ImportError(de.storageImportSchemaNewer);
  }
  if (typeof candidate.exportedAt !== 'string') throw new ImportError(de.storageImportInvalid);

  // The envelope says "this is not our file" — pick a different one. Everything below says "this *is*
  // our file and it is not complete" — fix this one. Different actions, so different sentences.
  const settings = normaliseSettings(candidate.settings);
  validateRows(candidate as unknown as Record<string, unknown>, EXPORT_ARRAYS);

  return { ...(candidate as SagsExport), settings };
}

/**
 * Replace the whole database with an export. Wholesale, not a merge: a restore is
 * "make this device be that device", and a half-merged vocabulary is worse than
 * either version of it.
 *
 * Older exports are migrated forward here as `schemaVersion` grows. There is nothing
 * to do at v1, but the seam exists so the first real change is routine.
 */
export async function applyImport(data: SagsExport): Promise<void> {
  const migrated = migrateExport(data);

  // Checked again here, cheaply. This function is exported and its only production caller lives in
  // another module, so it does not get to assume somebody else validated its input (C74).
  validateRows(migrated as unknown as Record<string, unknown>, EXPORT_ARRAYS);

  try {
    // One transaction: the clear and the writes together, so a failure leaves the old device standing.
    // Device facts survive either way — `meta` is not in the file, so clearing it would leave the device
    // with less than it started with, including the backup credentials the next backup needs (C52).
    await replacePersonStores(async (tx) => {
      await tx.objectStore('settings').put(migrated.settings, 'settings');

      for (const row of migrated.quickFires) {
        // Audio keys are dropped: the Cache API was not part of this file, so a key
        // pointing at audio that does not exist would make CachedAudio miss silently.
        const { audioKey: _dropped, ...rest } = row;
        await tx.objectStore('quickFires').put(rest);
      }
      for (const row of migrated.categories) await tx.objectStore('categories').put(row);
      for (const row of migrated.phrases) {
        const { audioKey: _dropped, ...rest } = row;
        await tx.objectStore('phrases').put(rest);
      }
      for (const row of migrated.abbrevs) await tx.objectStore('abbrevs').put(row);
      for (const [word, count] of migrated.personalUnigrams) {
        await tx.objectStore('personalUnigrams').put({ word, count, lastUsedAt: IMPORTED_LAST_USED });
      }
      for (const [prev, next, count] of migrated.personalBigrams) {
        await tx
      .objectStore('personalBigrams')
      .put({ prev, next, count, lastUsedAt: IMPORTED_LAST_USED });
      }
      for (const row of migrated.candidatePhrases) {
        await tx.objectStore('candidatePhrases').put(row);
      }
      for (const row of migrated.calibrationRuns) await tx.objectStore('calibration').put(row);
      for (const row of migrated.history) await tx.objectStore('history').put(row);
    });
  } catch (err) {
    // Validation makes this unreachable for anything the file can *express* — but the engine can still
    // refuse a write for reasons no shape check could predict: quota, a connection Safari terminated, a
    // value structured clone will not take. What must never reach a caregiver is an English `TypeError`
    // over a device whose state they cannot see. The transaction rolled back, so the German is true.
    console.error('[import] write failed after validation', err);
    throw new ImportError(de.storageImportFailed, undefined, err);
  }
}

/**
 * ── Forward-migrating a document (C78, DAT-12) ──
 *
 * One entry per schema version, mirroring the database migrations, because a restored device must end up
 * where the migrations would have put it. This used to stamp `schemaVersion` and transform nothing, and
 * the comment said "v1 → v2 goes here when v2 exists" long after v2 and v3 both existed — so importing a
 * v1 backup reinstalled the C42 weight vector and turned prediction off. Permanently: the device is
 * already at the current version, so `runMigrations` never visits that row again.
 *
 * The rules are the database's rules, deliberately, and each is only correct because of *when* it runs:
 * v2 replaces the weights only when they are still the old default, and v3 sets a flag only for a
 * document that never ran v3. `EXPORT_MIGRATED_VERSIONS` is asserted against `MIGRATIONS`, so the two
 * lists cannot drift apart a second time.
 */
interface ExportMigration {
  version: number;
  /** Applied when the document predates `version`. Returns the settings it wants written. */
  up: (settings: Settings) => Settings;
}

/** The weight vector v2 corrected. A caregiver who tuned theirs is left alone. */
const V1_DEFAULT_WEIGHTS = [1, 3, 1, 3, 1, 1];

const EXPORT_MIGRATIONS: readonly ExportMigration[] = [
  { version: 1, up: (settings) => settings },
  {
    version: 2,
    up: (settings) => {
      const current = settings.predictionWeights;
      const untouched =
        Array.isArray(current) &&
        current.length === V1_DEFAULT_WEIGHTS.length &&
        current.every((value, index) => value === V1_DEFAULT_WEIGHTS[index]);
      if (!untouched) return settings;
      return { ...settings, predictionWeights: [...DEFAULT_SETTINGS.predictionWeights] };
    },
  },
  {
    version: 3,
    // The database's v3 says no future migration may set this flag again — which is about devices that
    // have already run it, not about a document that never did. A `false` in a v3 document is a
    // caregiver's decision and stays.
    up: (settings) => ({ ...settings, predictionEnabled: true }),
  },
];

/** Exported for the guard test that keeps this list and `MIGRATIONS` in step. */
export const EXPORT_MIGRATED_VERSIONS: readonly number[] = EXPORT_MIGRATIONS.map(
  (migration) => migration.version,
);

export function migrateExport(data: SagsExport): SagsExport {
  if (data.schemaVersion === SCHEMA_VERSION) return data;

  let settings = data.settings;
  for (const migration of EXPORT_MIGRATIONS) {
    if (migration.version > data.schemaVersion) settings = migration.up(settings);
  }

  // The row's own version too: it stayed at 1 while the document said 3, so the two disagreed.
  return {
    ...data,
    schemaVersion: SCHEMA_VERSION,
    settings: { ...settings, schemaVersion: SCHEMA_VERSION },
  };
}
